import { createFileRoute, Link } from '@tanstack/react-router'
import { ChevronRightIcon, SplitIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useDateWindow } from '@/components/date-range-picker'
import { DeltaInline } from '@/components/delta'
import { Pager } from '@/components/pager'
import { Panel } from '@/components/panel'
import { formatCount, formatExact, formatPosition, formatRate, shortenUrl } from '@/lib/format'
import {
  DEFAULT_STRIKING_BAND,
  type KeywordPage,
  STRIKING_BANDS,
  type StrikingBand,
  type StrikingData,
  type StrikingRow,
  useOpportunities,
} from '@/lib/site-data'
import { cn } from '@/lib/utils'
import { SectionHeader, SiteSectionBody, TableSkeleton, useSiteScope } from '../../-section'

const PAGE_SIZE = 25

/**
 * Two of the site's own urls answering one search, rather than one url that
 * happens to have a runner-up. Below this share the winner is not winning: the
 * fix is to pick a page, not to strengthen both.
 */
const SPLIT_SHARE = 0.6

function isSplit(page: KeywordPage): boolean {
  return page.pages > 1 && page.share < SPLIT_SHARE
}

function parseBand(value: unknown): StrikingBand | undefined {
  return STRIKING_BANDS.find((band) => band.key === value)?.key
}

export const Route = createFileRoute('/_app/$org/$host/keywords/_list/low-hanging')({
  // The band changes what the page means, so it belongs in the url: a link to
  // "the keywords at the bottom of page one" has to open on those.
  validateSearch: (search: Record<string, unknown>): { band?: StrikingBand } => {
    const band = parseBand(search.band)
    return band ? { band } : {}
  },
  component: LowHangingPage,
})

/**
 * The keywords a small push would pay for, in the order they should be pushed.
 *
 * One table, because the ordering is the whole answer and a shortlist above it
 * was the same rows said twice. Everything here is the site's own arithmetic:
 * the band is where a keyword ranks, and what a place there is worth is the
 * rate this site earns at the target positions — which is why the footer states
 * that rate rather than a paragraph explaining it.
 */
function LowHangingPage() {
  const navigate = Route.useNavigate()
  const { org, host, range, site } = useSiteScope()
  const { band = DEFAULT_STRIKING_BAND } = Route.useSearch()
  const [offset, setOffset] = useState(0)
  const { data, isPending, isFetching } = useOpportunities(site, range, band, {
    limit: PAGE_SIZE,
    offset,
  })
  useDateWindow(data?.range)
  const best = bestUplift(data?.rows ?? [])

  // A different window is a different question, not page three of the old one,
  // and the control that changes it is no longer on this page to reset it.
  // biome-ignore lint/correctness/useExhaustiveDependencies: resetting on range is the point
  useEffect(() => setOffset(0), [range])

  return (
    <>
      <SectionHeader
        title="Low hanging"
        filter={
          <div className="inline-flex h-9 items-center rounded-md border border-border bg-card p-0.5">
            {STRIKING_BANDS.map((option) => (
              <button
                key={option.key}
                type="button"
                aria-pressed={band === option.key}
                onClick={() => {
                  // A new band is a different question, not page three of the
                  // old one.
                  setOffset(0)
                  navigate({ search: (prev) => ({ ...prev, band: option.key }), replace: true })
                }}
                className={cn(
                  'cursor-pointer rounded-[4px] px-2.5 py-1 text-[13px] transition-colors',
                  band === option.key
                    ? 'bg-accent text-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
        }
      />

      <SiteSectionBody
        org={org}
        host={host}
        range={range}
        result={data}
        isPending={isPending}
        rowCount={data?.total ?? 0}
        skeleton={<TableSkeleton />}
      >
        {data && (
          <Panel flush className={cn(isFetching && 'opacity-70 transition-opacity')}>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-[13px]">
                <thead>
                  <tr className="border-b border-border bg-secondary/30">
                    <th className="w-9 py-2 pr-2 pl-3 text-right">
                      <span className="mono-label text-muted-foreground/70">#</span>
                    </th>
                    <th className="w-[38%] py-2 pr-3 text-left">
                      <span className="mono-label text-muted-foreground">Keyword</span>
                    </th>
                    <th className="w-[26%] py-2 pr-3 text-left">
                      <span className="mono-label text-muted-foreground">Page</span>
                    </th>
                    <Head>Shown</Head>
                    <Head>Clicks</Head>
                    <Head>Pos.</Head>
                    <Head>Gain</Head>
                    <th className="w-8" aria-label="Open" />
                  </tr>
                </thead>

                <tbody>
                  {data.rows.map((row, index) => (
                    <Row
                      key={row.queryId}
                      row={row}
                      rank={offset + index + 1}
                      bar={barWidth(row.uplift, best)}
                      org={org}
                      host={host}
                      range={range}
                      onSelect={() =>
                        navigate({
                          to: '/$org/$host/keywords/$queryId',
                          params: { org, host, queryId: String(row.queryId) },
                          search: { range },
                        })
                      }
                    />
                  ))}
                </tbody>
              </table>
            </div>

            <Footer data={data} offset={offset} onOffsetChange={setOffset} />
          </Panel>
        )}
      </SiteSectionBody>
    </>
  )
}

function Head({ children }: { children: React.ReactNode }) {
  return (
    <th className="w-[92px] px-3 py-2 text-right">
      <span className="mono-label text-muted-foreground">{children}</span>
    </th>
  )
}

/**
 * A row is one line, because the page is a worklist and a worklist is read down
 * its left edge. The url gets a column rather than a second line for the same
 * reason: the action is always on a page, so it is a value to compare and not a
 * footnote to the keyword.
 */
function Row({
  row,
  rank,
  bar,
  org,
  host,
  range,
  onSelect,
}: {
  row: StrikingRow
  rank: number
  /** 0–100, this row's gain against the best on the page. */
  bar: number
  org: string
  host: string
  range: ReturnType<typeof useSiteScope>['range']
  onSelect: () => void
}) {
  return (
    <tr
      className="group cursor-pointer border-b border-border last:border-b-0 hover:bg-secondary/40"
      onClick={onSelect}
    >
      <td className="py-2 pr-2 pl-3 text-right align-middle">
        <span className="font-mono text-[11px] text-muted-foreground/70 tabular">
          {String(rank).padStart(2, '0')}
        </span>
      </td>

      {/* The list is ordered by what moving the keyword is worth and cannot be
          re-sorted, so the bar behind the label is the ordering made visible. */}
      <td className="relative max-w-0 py-2 pr-3">
        <span
          className="absolute inset-y-1 left-0 rounded-r-[2px] bg-plum/10"
          style={{ width: `${bar}%` }}
          aria-hidden
        />
        <Link
          to="/$org/$host/keywords/$queryId"
          params={{ org, host, queryId: String(row.queryId) }}
          search={{ range }}
          onClick={(event) => event.stopPropagation()}
          className="relative block truncate font-mono text-[12.5px] underline-offset-2 hover:underline"
          title={row.query}
        >
          {row.query}
        </Link>
      </td>

      <td className="max-w-0 py-2 pr-3">
        <PageCell row={row} org={org} host={host} range={range} />
      </td>

      <Cell>
        <span title={formatExact(row.impressions)}>{formatCount(row.impressions)}</span>
      </Cell>

      <Cell>{formatCount(row.clicks)}</Cell>

      <Cell>
        <span className="inline-flex items-baseline justify-end gap-1.5">
          {formatPosition(row.position)}
          <DeltaInline value={row.delta?.position ?? null} higherIsBetter={false} absolute />
        </span>
      </Cell>

      <Cell>
        <Gain value={row.uplift} />
      </Cell>

      <td className="pr-3 text-right align-middle">
        <ChevronRightIcon
          className="inline size-3.5 text-muted-foreground/40 transition-colors group-hover:text-plum"
          aria-hidden
        />
      </td>
    </tr>
  )
}

/** Which url to work on — or that the question is which url, not what to do
 * with it. A keyword the [query, page] set never named gets nothing rather than
 * the site's best guess at which page it must have been. */
function PageCell({
  row,
  org,
  host,
  range,
}: {
  row: StrikingRow
  org: string
  host: string
  range: ReturnType<typeof useSiteScope>['range']
}) {
  const page = row.page
  if (!page) return <span className="font-mono text-[11.5px] text-muted-foreground/60">—</span>

  if (isSplit(page)) {
    return (
      <span className="flex items-center gap-1 font-mono text-[11.5px] text-warning tabular">
        <SplitIcon className="size-3 shrink-0" aria-hidden />
        {page.pages} pages split
      </span>
    )
  }

  return (
    <Link
      to="/$org/$host/pages/$pageId"
      params={{ org, host, pageId: String(page.pageId) }}
      search={{ range }}
      onClick={(event) => event.stopPropagation()}
      className="block truncate font-mono text-[11.5px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
      title={page.url}
    >
      {shortenUrl(page.url, host)}
    </Link>
  )
}

function Cell({ children }: { children: React.ReactNode }) {
  return (
    <td className="px-3 py-2 text-right align-middle font-mono whitespace-nowrap tabular">
      {children}
    </td>
  )
}

/** Under a click is not a finding. Rounding it up to "+1" would turn the
 * arithmetic's noise floor into a promise. */
function Gain({ value }: { value: number | null }) {
  if (value === null || value < 1) return <span className="text-muted-foreground/60">—</span>
  return <span>+{formatCount(Math.round(value))}</span>
}

/**
 * The count, what the band is worth, and the rate it was measured against —
 * one line in the machine's voice. The benchmark belongs beside the number it
 * produced: it is a property of the estimate, not a caveat under it.
 */
function Footer({
  data,
  offset,
  onOffsetChange,
}: {
  data: StrikingData
  offset: number
  onOffsetChange: (next: number) => void
}) {
  const worth = data.uplift !== null && data.uplift >= 1 ? Math.round(data.uplift) : null

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-t border-border px-4 py-2.5 font-mono text-[11px] text-muted-foreground tabular">
      <span>{formatExact(data.total)} keywords</span>
      {worth !== null && (
        <>
          <Dot />
          <span className="text-foreground">at least +{formatCount(worth)} clicks</span>
        </>
      )}
      <Dot />
      {data.target.ctr === null ? (
        <span>
          no rate at {data.target.from}–{data.target.to}, ordered by impressions
        </span>
      ) : (
        <span>
          vs {formatRate(data.target.ctr)} at {data.target.from}–{data.target.to}
        </span>
      )}
      <Pager
        offset={offset}
        limit={data.paging.limit}
        total={data.total}
        onOffsetChange={onOffsetChange}
        className="ml-auto"
      />
    </div>
  )
}

function Dot() {
  return (
    <span className="text-border-strong" aria-hidden>
      ·
    </span>
  )
}

/** The page's own extent, not the site's: page four of a long tail should still
 * show a readable spread instead of four invisible stubs. */
function bestUplift(rows: StrikingRow[]): number {
  return rows.reduce((max, row) => Math.max(max, row.uplift ?? 0), 0)
}

function barWidth(uplift: number | null, best: number): number {
  if (uplift === null || uplift < 1 || best <= 0) return 0
  return Math.min(100, (uplift / best) * 100)
}
